import React, { useEffect, useRef, useState, useCallback } from "react";
import * as THREE from "three";
import { Dna, Play, Pause, Info, X, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

// Deterministic glacial hue per ship/compartment so each craft owns a colour band.
function shipHue(name) {
  let h = 0;
  const s = name || "AVA self";
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return 168 + (h % 100); // 168..268 -> glacier teal -> periwinkle
}
const shipColorHex = (name) => new THREE.Color(`hsl(${shipHue(name)}, 68%, 62%)`);
const shipColorCss = (name) => `hsl(${shipHue(name)}, 68%, 62%)`;

const PAIR_META = {
  "E-S": { label: "Episodic \u21c4 Semantic", rung: 0x8fd0d6, css: "text-[#8fd0d6]" },
  "H-B": { label: "Hebbian \u21c4 Bayesian", rung: 0xaea8e0, css: "text-[#aea8e0]" },
};

const HIPPO_HEX = 0x9ee3ea; // fast strand - hippocampus
const NEO_HEX = 0x93b4de;   // slow strand - neocortex

export default function OctonDNA() {
  const mountRef = useRef(null);
  const stateRef = useRef({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playhead, setPlayhead] = useState(1); // 0..1 sequence reveal
  const [playing, setPlaying] = useState(false);
  const [selected, setSelected] = useState(null);
  const [hover, setHover] = useState(null);
  const [showExplainer, setShowExplainer] = useState(false);
  const [regen, setRegen] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/brain/dna")
      .then((r) => setData(r.data))
      .catch(() => setData({ bases: [], cycles: [], counts: {} }))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  // live neurogenesis ticker (refreshes every 60s)
  useEffect(() => {
    const pull = () => api.get("/brain/regeneration").then((r) => setRegen(r.data)).catch(() => {});
    pull();
    const id = setInterval(pull, 60000);
    return () => clearInterval(id);
  }, []);

  // ── Build the 3D helix once the bases arrive ────────────────────────────────
  useEffect(() => {
    const bases = data?.bases || [];
    const mount = mountRef.current;
    if (!mount || !bases.length) return;

    const width = mount.clientWidth || 600;
    const height = mount.clientHeight || 380;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(42, width / height, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    mount.appendChild(renderer.domElement);
    renderer.domElement.style.cursor = "grab";

    scene.add(new THREE.AmbientLight(0xbfe6ff, 0.7));
    const key = new THREE.PointLight(0x9ee3ea, 1.4, 400);
    key.position.set(30, 20, 60);
    scene.add(key);
    const rim = new THREE.PointLight(0xaea8e0, 0.9, 400);
    rim.position.set(-40, -20, 30);
    scene.add(rim);

    const N = bases.length;
    const rise = 1.18;
    const R = 4.6;
    const perTurn = 8;
    const sphereGeo = new THREE.SphereGeometry(0.62, 20, 20);

    const group = new THREE.Group();
    group.rotation.x = -0.06;
    scene.add(group);

    // time-normalise each base for the scrubber
    const times = bases.map((b) => Date.parse(b.ts || "") || 0);
    const tmin = Math.min(...times.filter(Boolean), Date.now());
    const tmax = Math.max(...times, tmin + 1);
    const tNorm = bases.map((b) => {
      const t = Date.parse(b.ts || "") || tmin;
      return (t - tmin) / (tmax - tmin || 1);
    });

    const connect = (a, bpt, radius, mat) => {
      const dir = new THREE.Vector3().subVectors(bpt, a);
      const len = dir.length() || 0.001;
      const geo = new THREE.CylinderGeometry(radius, radius, len, 6);
      const m = new THREE.Mesh(geo, mat);
      m.position.copy(a).add(bpt).multiplyScalar(0.5);
      m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir.clone().normalize());
      return m;
    };

    const hippoMat = new THREE.MeshStandardMaterial({ color: HIPPO_HEX, emissive: HIPPO_HEX, emissiveIntensity: 0.5, roughness: 0.5, metalness: 0.1 });
    const neoMat = new THREE.MeshStandardMaterial({ color: NEO_HEX, emissive: NEO_HEX, emissiveIntensity: 0.35, roughness: 0.6, metalness: 0.1 });
    const rungMats = {
      "E-S": new THREE.MeshStandardMaterial({ color: PAIR_META["E-S"].rung, emissive: PAIR_META["E-S"].rung, emissiveIntensity: 0.4, roughness: 0.5 }),
      "H-B": new THREE.MeshStandardMaterial({ color: PAIR_META["H-B"].rung, emissive: PAIR_META["H-B"].rung, emissiveIntensity: 0.4, roughness: 0.5 }),
    };

    const spheres = []; // {mesh, i, base}
    const rungs = [];    // {mesh, i}
    const bbLeft = [];   // segment i connects i-1 -> i
    const bbRight = [];
    const positions = [];

    for (let i = 0; i < N; i++) {
      const b = bases[i];
      const turn = i * (Math.PI * 2 / perTurn);
      const y = (i - (N - 1) / 2) * rise;
      const lp = new THREE.Vector3(R * Math.cos(turn), y, R * Math.sin(turn));
      const rp = new THREE.Vector3(R * Math.cos(turn + Math.PI), y, R * Math.sin(turn + Math.PI));
      positions.push({ lp, rp });

      const col = shipColorHex(b.ship);
      const freshBoost = b.fresh ? 0.95 : (0.35 + 0.4 * (b.strength || 0.4));
      const lm = new THREE.MeshStandardMaterial({ color: col, emissive: col, emissiveIntensity: freshBoost, roughness: 0.35, metalness: 0.15 });
      const rm = new THREE.MeshStandardMaterial({ color: col.clone().offsetHSL(0, -0.05, 0.05), emissive: col, emissiveIntensity: b.fresh ? 0.8 : 0.3, roughness: 0.4, metalness: 0.15 });
      const sc = 0.7 + 0.7 * (b.strength || 0.4);

      const ls = new THREE.Mesh(sphereGeo, lm); ls.position.copy(lp); ls.scale.setScalar(sc); ls.userData = { i, side: "left" };
      const rs = new THREE.Mesh(sphereGeo, rm); rs.position.copy(rp); rs.scale.setScalar(sc); rs.userData = { i, side: "right" };
      group.add(ls); group.add(rs);
      spheres.push({ mesh: ls, i, base: b, baseScale: sc, fresh: b.fresh }, { mesh: rs, i, base: b, baseScale: sc, fresh: b.fresh });

      const rung = connect(lp, rp, 0.11, rungMats[b.pair] || rungMats["E-S"]);
      group.add(rung); rungs.push({ mesh: rung, i });

      if (i > 0) {
        const segL = connect(positions[i - 1].lp, lp, 0.16, hippoMat);
        const segR = connect(positions[i - 1].rp, rp, 0.16, neoMat);
        group.add(segL); group.add(segR);
        bbLeft.push({ mesh: segL, i }); bbRight.push({ mesh: segR, i });
      }
    }

    const totalH = N * rise;
    const camZ = (totalH / 2) / Math.tan((42 / 2) * Math.PI / 180) * 1.02 + 8;
    camera.position.set(0, 0, camZ);
    camera.lookAt(0, 0, 0);

    const raycaster = new THREE.Raycaster();
    const pointer = new THREE.Vector2();
    let hoveredIdx = -1;
    let pointerInside = false;

    const onMove = (ev) => {
      const rect = renderer.domElement.getBoundingClientRect();
      pointer.x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointer, camera);
      const pick = spheres.filter((s) => s.mesh.visible).map((s) => s.mesh);
      const hits = raycaster.intersectObjects(pick, false);
      if (hits.length) {
        const idx = hits[0].object.userData.i;
        if (idx !== hoveredIdx) {
          hoveredIdx = idx;
          setHover({ base: bases[idx], x: ev.clientX - rect.left, y: ev.clientY - rect.top });
          renderer.domElement.style.cursor = "pointer";
        } else {
          setHover((h) => (h ? { ...h, x: ev.clientX - rect.left, y: ev.clientY - rect.top } : h));
        }
      } else if (hoveredIdx !== -1) {
        hoveredIdx = -1; setHover(null); renderer.domElement.style.cursor = "grab";
      }
    };
    const onClick = () => { if (hoveredIdx !== -1) setSelected(bases[hoveredIdx]); };
    const onLeave = () => { hoveredIdx = -1; pointerInside = false; setHover(null); renderer.domElement.style.cursor = "grab"; };
    const onEnter = () => { pointerInside = true; };
    renderer.domElement.addEventListener("pointermove", onMove);
    renderer.domElement.addEventListener("click", onClick);
    renderer.domElement.addEventListener("pointerleave", onLeave);
    renderer.domElement.addEventListener("pointerenter", onEnter);

    let raf;
    let running = true;
    const clock = new THREE.Clock();
    const animate = () => {
      if (!running) return;
      raf = requestAnimationFrame(animate);
      const el = clock.getElapsedTime();
      if (!pointerInside) group.rotation.y += 0.0032;
      spheres.forEach((s) => {
        const isHot = s.i === hoveredIdx || (stateRef.current.selectedIdx === s.i);
        const fresh = s.fresh;
        const pulse = isHot ? 1 + 0.18 * Math.sin(el * 6) : (fresh ? 1 + 0.12 * Math.sin(el * 3 + s.i) : 1);
        s.mesh.scale.setScalar(s.baseScale * (isHot ? 1.7 : fresh ? 1.25 : 1) * pulse);
        s.mesh.material.emissiveIntensity = isHot ? 1.1 : (fresh ? 0.7 + 0.3 * Math.abs(Math.sin(el * 3 + s.i)) : 0.3 + 0.4 * (s.base.strength || 0.4));
      });
      renderer.render(scene, camera);
    };
    animate();

    const applyReveal = (ph) => {
      spheres.forEach((s) => { s.mesh.visible = tNorm[s.i] <= ph + 1e-6; });
      rungs.forEach((r) => { r.mesh.visible = tNorm[r.i] <= ph + 1e-6; });
      bbLeft.forEach((seg) => { seg.mesh.visible = tNorm[seg.i] <= ph + 1e-6 && tNorm[seg.i - 1] <= ph + 1e-6; });
      bbRight.forEach((seg) => { seg.mesh.visible = tNorm[seg.i] <= ph + 1e-6 && tNorm[seg.i - 1] <= ph + 1e-6; });
    };

    const onResize = () => {
      const w = mount.clientWidth || width, h = mount.clientHeight || height;
      camera.aspect = w / h; camera.updateProjectionMatrix(); renderer.setSize(w, h);
    };
    const ro = new ResizeObserver(onResize);
    ro.observe(mount);

    stateRef.current = { applyReveal, tNorm, cleanup: null, selectedIdx: -1 };
    applyReveal(playhead);

    stateRef.current.cleanup = () => {
      running = false;
      cancelAnimationFrame(raf);
      ro.disconnect();
      renderer.domElement.removeEventListener("pointermove", onMove);
      renderer.domElement.removeEventListener("click", onClick);
      renderer.domElement.removeEventListener("pointerleave", onLeave);
      renderer.domElement.removeEventListener("pointerenter", onEnter);
      renderer.dispose();
      sphereGeo.dispose();
      spheres.forEach((s) => s.mesh.material.dispose());
      [hippoMat, neoMat, rungMats["E-S"], rungMats["H-B"]].forEach((m) => m.dispose());
      [...rungs, ...bbLeft, ...bbRight].forEach((o) => o.mesh.geometry.dispose());
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
    };
    return () => stateRef.current.cleanup && stateRef.current.cleanup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // reveal on scrub
  useEffect(() => { stateRef.current.applyReveal && stateRef.current.applyReveal(playhead); }, [playhead]);
  // keep selected highlight in sync with the render loop
  useEffect(() => {
    const bases = data?.bases || [];
    stateRef.current.selectedIdx = selected ? bases.findIndex((b) => b.i === selected.i) : -1;
  }, [selected, data]);

  // auto-play the sequencer
  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => {
      setPlayhead((p) => {
        const next = +(p + 0.012).toFixed(3);
        if (next >= 1) { setPlaying(false); return 1; }
        return next;
      });
    }, 60);
    return () => clearInterval(id);
  }, [playing]);

  const bases = data?.bases || [];
  const cycles = data?.cycles || [];
  const counts = data?.counts || {};
  const revealedCount = bases.filter((b, idx) => {
    const t = stateRef.current.tNorm ? stateRef.current.tNorm[idx] : 1;
    return t <= playhead + 1e-6;
  }).length;
  const revealedDate = (() => {
    const revealed = bases.filter((_, idx) => (stateRef.current.tNorm ? stateRef.current.tNorm[idx] : 1) <= playhead + 1e-6);
    const last = revealed[revealed.length - 1];
    return last?.ts ? last.ts.slice(0, 10) : "\u2014";
  })();

  return (
    <section data-testid="octon-dna" className="rounded-2xl border border-cyan-400/15 bg-gradient-to-b from-[#061019]/80 to-[#04070c]/90 overflow-hidden mb-6">
      <div className="flex items-center justify-between flex-wrap gap-2 px-4 pt-3.5 pb-2">
        <div>
          <h3 className="font-display font-bold text-white text-sm flex items-center gap-2">
            <Dna className="w-4 h-4 text-cyan-300" /> OCTON DNA
          </h3>
          <p className="text-[10px] text-slate-500 mt-0.5">
            The hippocampal <span className="text-[#9ee3ea]">&harr;</span> neocortical bridge, sequenced &middot; {counts.concepts ?? "\u2014"} concepts &middot; {counts.synapses ?? "\u2014"} synapses &middot; {counts.snapshots ?? 0} nightly snapshots
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <button data-testid="octon-dna-explainer-toggle" onClick={() => setShowExplainer((v) => !v)}
            className={`px-2.5 py-1.5 rounded-lg text-[10px] font-mono-data uppercase tracking-wider border transition-colors ${showExplainer ? "bg-cyan-400/15 text-cyan-300 border-cyan-400/30" : "text-slate-400 border-white/10 hover:text-cyan-300"}`}>
            <Info className="w-3 h-3 inline mr-1" />The science
          </button>
          <button data-testid="octon-dna-refresh" onClick={load} className="p-1.5 rounded-lg text-slate-500 hover:text-cyan-300 transition-colors">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="relative">
        <div ref={mountRef} data-testid="octon-dna-canvas" className="w-full h-[340px] sm:h-[400px]" />
        {loading && <div className="absolute inset-0 flex items-center justify-center text-[11px] text-cyan-300/70 animate-pulse">Sequencing the genome&hellip;</div>}
        {!loading && !bases.length && <div className="absolute inset-0 flex items-center justify-center text-[11px] text-slate-500">No sequence yet - the helix appears once concepts and synapses have formed.</div>}

        {/* legend */}
        <div className="absolute top-3 left-3 space-y-1.5 pointer-events-none">
          <div className="flex items-center gap-1.5"><span className="w-3 h-1 rounded-full" style={{ background: "#9ee3ea" }} /><span className="text-[9px] font-mono-data text-slate-400">Hippocampus strand (episodic)</span></div>
          <div className="flex items-center gap-1.5"><span className="w-3 h-1 rounded-full" style={{ background: "#93b4de" }} /><span className="text-[9px] font-mono-data text-slate-400">Neocortex strand (semantic)</span></div>
          <div className="flex items-center gap-1.5"><span className="w-3 h-1 rounded-full" style={{ background: "#8fd0d6" }} /><span className="text-[9px] font-mono-data text-slate-400">E&ndash;S base-pair</span></div>
          <div className="flex items-center gap-1.5"><span className="w-3 h-1 rounded-full" style={{ background: "#aea8e0" }} /><span className="text-[9px] font-mono-data text-slate-400">H&ndash;B base-pair</span></div>
          <p className="text-[8px] font-mono-data text-slate-600 max-w-[150px] leading-tight">bead colour = the craft that taught that stretch</p>
        </div>

        {/* live neurogenesis ticker */}
        {regen && (
          <div data-testid="octon-dna-live-ticker" className="absolute bottom-3 left-3 flex items-center gap-2 rounded-lg border border-emerald-400/25 bg-black/60 backdrop-blur px-2.5 py-1.5 pointer-events-none">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-emerald-400 opacity-70" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
            </span>
            <span className="text-[10px] font-mono-data text-emerald-300">+{regen.neurogenesis?.born_24h ?? 0} neurons</span>
            <span className="text-[10px] font-mono-data text-slate-400">{regen.neurogenesis?.fired_24h ?? 0} fired · 24h</span>
          </div>
        )}

        {/* hover tooltip */}
        {hover && (
          <div className="absolute z-20 pointer-events-none rounded-lg border border-white/15 bg-black/85 backdrop-blur px-2.5 py-1.5 shadow-xl max-w-[220px]"
            style={{ left: Math.min(hover.x + 12, (mountRef.current?.clientWidth || 600) - 220), top: Math.max(hover.y - 10, 6) }}>
            <p className="text-[9px] font-mono-data uppercase tracking-wider" style={{ color: shipColorCss(hover.base.ship) }}>
              {PAIR_META[hover.base.pair]?.label} &middot; {hover.base.ship}
            </p>
            <p className="text-[11px] text-slate-200 leading-snug mt-0.5">{hover.base.concept}</p>
            <p className="text-[8px] font-mono-data text-slate-500 mt-0.5">click to open the consolidation</p>
          </div>
        )}

        {/* drill-in card */}
        {selected && (
          <div data-testid="octon-dna-base-card" className="absolute z-30 top-3 right-3 w-[280px] rounded-xl border border-cyan-400/25 bg-[#050b12]/95 backdrop-blur-md p-3.5 shadow-2xl">
            <div className="flex items-start justify-between gap-2">
              <span className="px-2 py-0.5 rounded border text-[9px] font-mono-data uppercase tracking-wider" style={{ color: PAIR_META[selected.pair]?.rung ? `#${PAIR_META[selected.pair].rung.toString(16)}` : "#8fd0d6", borderColor: "rgba(255,255,255,.15)" }}>
                {PAIR_META[selected.pair]?.label}
              </span>
              <button data-testid="octon-dna-base-card-close" onClick={() => setSelected(null)} className="text-slate-500 hover:text-white"><X className="w-3.5 h-3.5" /></button>
            </div>
            <p className="text-[12px] text-white font-semibold leading-snug mt-2">{selected.concept}</p>
            <p className="text-[9px] font-mono-data mt-0.5" style={{ color: shipColorCss(selected.ship) }}>taught by {selected.ship}</p>

            <div className="mt-3 space-y-2.5">
              <div className="rounded-lg border border-[#9ee3ea]/20 bg-[#9ee3ea]/[0.05] p-2">
                <p className="text-[9px] font-mono-data uppercase tracking-wider text-[#9ee3ea]">{selected.left.base} &middot; {selected.left.kind}</p>
                <p className="text-[10px] text-slate-300 leading-snug mt-1">{selected.left.detail}</p>
              </div>
              <div className="flex justify-center"><span className="text-[9px] font-mono-data text-slate-600">consolidated &darr;&uarr; paired</span></div>
              <div className="rounded-lg border border-[#93b4de]/20 bg-[#93b4de]/[0.05] p-2">
                <p className="text-[9px] font-mono-data uppercase tracking-wider text-[#93b4de]">{selected.right.base} &middot; {selected.right.kind}</p>
                <p className="text-[10px] text-slate-300 leading-snug mt-1">{selected.right.detail}</p>
              </div>
            </div>
            <div className="mt-2.5 flex items-center gap-2">
              <span className="text-[9px] font-mono-data text-slate-500">strength</span>
              <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full rounded-full bg-cyan-400/70" style={{ width: `${Math.round((selected.strength || 0) * 100)}%` }} />
              </div>
              <span className="text-[9px] font-mono-data text-cyan-300">{selected.strength}</span>
            </div>
            <p className="text-[8px] font-mono-data text-slate-600 mt-1.5">formed {selected.ts ? selected.ts.slice(0, 10) : "\u2014"}</p>
          </div>
        )}
      </div>

      {/* sequencer scrubber */}
      <div className="px-4 py-3 border-t border-white/5">
        <div className="flex items-center gap-3">
          <button data-testid="octon-dna-play" onClick={() => { if (playhead >= 1) setPlayhead(0); setPlaying((p) => !p); }}
            className="p-2 rounded-lg bg-cyan-400/10 hover:bg-cyan-400/20 border border-cyan-400/25 text-cyan-300 transition-colors">
            {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
          </button>
          <div className="flex-1 relative">
            <input data-testid="octon-dna-scrubber" type="range" min={0} max={1} step={0.001} value={playhead}
              onChange={(e) => { setPlaying(false); setPlayhead(parseFloat(e.target.value)); }}
              className="w-full accent-cyan-400 cursor-pointer" />
            {/* real nightly-snapshot markers */}
            <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 pointer-events-none">
              {cycles.map((c, i) => {
                const times = bases.map((b) => Date.parse(b.ts || "") || 0).filter(Boolean);
                const tmin = Math.min(...times, Date.now()), tmax = Math.max(...times, tmin + 1);
                const pos = ((Date.parse(c.built_at) - tmin) / (tmax - tmin || 1)) * 100;
                if (pos < 0 || pos > 100) return null;
                return <span key={i} title={`snapshot ${c.built_at?.slice(0, 10)}`} className="absolute w-0.5 h-2 bg-amber-300/60 rounded-full" style={{ left: `${pos}%` }} />;
              })}
            </div>
          </div>
          <div className="text-right whitespace-nowrap">
            <p className="text-[10px] font-mono-data text-cyan-300" data-testid="octon-dna-seq-count">{revealedCount}/{bases.length} bases</p>
            <p className="text-[8px] font-mono-data text-slate-500">as of {revealedDate}</p>
          </div>
        </div>
      </div>

      {/* explainer */}
      {showExplainer && (
        <div data-testid="octon-dna-explainer" className="px-4 pb-4 pt-1 border-t border-white/5 text-[11px] text-slate-300 leading-relaxed space-y-2">
          <p><span className="text-cyan-300 font-semibold">Why a double helix?</span> AVA's brain runs on <span className="text-cyan-200">Complementary Learning Systems</span> - the same principle behind the human hippocampus and neocortex. DNA holds its information in two complementary strands zipped together by base-pairing; OCTON's mind holds its knowledge the exact same way.</p>
          <p><span className="text-[#9ee3ea] font-semibold">Hippocampus (left strand):</span> the fast learner - vivid, one-shot episodes of what just happened. <span className="text-[#93b4de] font-semibold">Neocortex (right strand):</span> the slow learner - stable schemas distilled from thousands of episodes. Every night AVA <span className="text-cyan-200">replays</span> episodes and gradually teaches the cortex - that nightly dialogue is the bridge, drawn here as each rung of the helix.</p>
          <p>The genome spells two complementary base-pairs: <span className="text-[#8fd0d6] font-semibold">E&ndash;S</span> (Episodic &harr; Semantic) and <span className="text-[#aea8e0] font-semibold">H&ndash;B</span> (Hebbian &harr; Bayesian) - the four letters of AVA's <span className="text-cyan-200">HNHB</span> architecture. Each bead's colour marks which craft in the fleet taught that stretch of the mind. Press play to watch the genome grow across real nightly snapshots, or click any rung to open the exact consolidation behind it.</p>
        </div>
      )}
    </section>
  );
}
